// ** React Imports
import { forwardRef } from 'react';

// ** Third Party Components
import classnames from 'classnames';
import Proptypes from 'prop-types';
import { Badge } from 'reactstrap';

// ** Types
interface AvatarProps {
  color?: string;
  className?: string;
  imgClassName?: string;
  initials?: boolean;
  size?: string;
  badgeUp?: boolean;
  content?: string;
  icon?: React.ReactNode;
  badgeColor?: string;
  badgeText?: string;
  img?: string | false;
  imgHeight?: string | number;
  imgWidth?: string | number;
  status?: string;
  tag?: React.ElementType;
  contentStyles?: React.CSSProperties;
  children?: React.ReactNode;
  // Additional props found in usage
  id?: string;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
}

const Avatar = forwardRef<any, AvatarProps>((props, ref) => {
  // ** Props
  const {
    color,
    className,
    imgClassName,
    initials,
    size,
    badgeUp,
    content,
    icon,
    badgeColor,
    badgeText,
    img,
    imgHeight,
    imgWidth,
    status,
    tag: Tag = 'div',
    contentStyles,
    id,
    onClick,
    ...rest
  } = props;

  // ** Function to extract initials from content
  const getInitials = (str: string) => {
    const results: string[] = [];
    const wordArray = str.split(' ');
    wordArray.forEach((e) => {
      results.push(e[0]);
    });
    return results.join('');
  };

  return (
    <Tag
      className={classnames('avatar', {
        [className || '']: className,
        [`bg-${color}`]: color,
        [`avatar-${size}`]: size
      })}
      ref={ref}
      id={id}
      onClick={onClick}
      {...rest}
    >
      {img === false || img === undefined ? (
        <span
          className={classnames('avatar-content', {
            'position-relative': badgeUp
          })}
          style={{
            ...contentStyles,
            height: imgHeight && !size ? imgHeight : 32,
            width: imgWidth && !size ? imgWidth : 32
          }}
        >
          {initials ? getInitials(content || '') : content}

          {icon ? icon : null}
          {badgeUp ? (
            <Badge color={badgeColor ? badgeColor : 'primary'} className="badge-sm badge-up" pill>
              {badgeText ? badgeText : '0'}
            </Badge>
          ) : null}
        </span>
      ) : (
        <img
          className={classnames({
            [imgClassName || '']: imgClassName
          })}
          src={img}
          alt="avatarImg"
          height={imgHeight && !size ? imgHeight : 32}
          width={imgWidth && !size ? imgWidth : 32}
        />
      )}
      {status ? (
        <span
          className={classnames({
            [`avatar-status-${status}`]: status,
            [`avatar-status-${size}`]: size
          })}
        ></span>
      ) : null}
    </Tag>
  );
});

export default Avatar;

// ** PropTypes (simplified for TypeScript compatibility)
Avatar.propTypes = {
  img: Proptypes.any,
  className: Proptypes.string,
  imgClassName: Proptypes.string,
  initials: Proptypes.bool,
  content: Proptypes.string,
  size: Proptypes.string,
  badgeUp: Proptypes.bool,
  icon: Proptypes.node,
  contentStyles: Proptypes.object,
  badgeText: Proptypes.string,
  imgHeight: Proptypes.any,
  imgWidth: Proptypes.any,
  tag: Proptypes.any,
  status: Proptypes.string,
  badgeColor: Proptypes.string,
  color: Proptypes.string
};

// ** Default Props
Avatar.defaultProps = {
  tag: 'div'
};
